import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * Fetch all user DBQs for a claim
 */
export const useFetchUserDbqs = (claimId) => {
  return useQuery({
    queryKey: ['user-dbqs', claimId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/claim/${claimId}`);
      return response.data;
    },
    enabled: !!claimId,
  });
};

export const useFetchDbqClaimsAdmins = (enabled = true) => {
  return useQuery({
    queryKey: ['dbq-claims-admins'],
    queryFn: async () => {
      const response = await axios.get('/dbq/claims-admins');
      return response.data;
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });
};

/**
 * Fetch a single user DBQ
 */
export const useFetchUserDbq = (userDbqId, token = null) => {
  return useQuery({
    queryKey: ['user-dbq', userDbqId, token],
    queryFn: async () => {
      const url = token
        ? `/dbq/public/user-dbq/${userDbqId}/${token}`
        : `/dbq/user-dbq/${userDbqId}`;
      const response = await axios.get(url);
      return response.data;
    },
    enabled: !!userDbqId,
  });
};

/**
 * Fetch audit timeline for a single user DBQ
 * Available to DBQ request managers and DBQ form admins via RBAC
 */
export const useFetchUserDbqAudit = (userDbqId, enabled = true) => {
  return useQuery({
    queryKey: ['user-dbq-audit', userDbqId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/${userDbqId}/audit`);
      return response.data;
    },
    enabled: Boolean(userDbqId && enabled),
    refetchOnWindowFocus: false,
  });
};

/**
 * Create a new user DBQ
 */
export const useCreateUserDbq = (onSuccess) => {
  return useMutation({
    mutationFn: async ({
      claimId,
      dbqFormId,
      toeId,
      contention = null,
      requestContentions = undefined,
      requestNotes = null,
      providerUserId = null,
      qaReviewerUserId = null,
      selectedEvidenceFileIds = [],
      aiFieldGenerationSettings = undefined,
      evidenceReviewPrompt = '',
      medicalOpinionParameters = null,
      autoGenerateMedicalOpinion = true,
      moPrompt = '',
      saveAsDraft = false,
    }) => {
      const response = await axios.post('/dbq/user-dbq', {
        claimId,
        dbqFormId,
        toeId,
        contention,
        requestContentions,
        requestNotes,
        providerUserId,
        qaReviewerUserId,
        selectedEvidenceFileIds,
        aiFieldGenerationSettings,
        evidenceReviewPrompt,
        medicalOpinionParameters,
        autoGenerateMedicalOpinion,
        moPrompt,
        saveAsDraft,
      });
      return response.data;
    },
    onSuccess,
  });
};

/**
 * Update the request/workspace metadata for an existing user DBQ.
 */
export const useUpdateUserDbqRequest = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      toeId,
      contention,
      requestContentions,
      requestNotes,
      providerUserId,
      qaReviewerUserId,
      selectedEvidenceFileIds,
      aiFieldGenerationSettings,
      evidenceReviewPrompt,
      medicalOpinionParameters,
      autoGenerateMedicalOpinion,
      moPrompt,
      saveAsDraft,
      submitRequest,
    }) => {
      const payload = {};
      if (toeId !== undefined) payload.toeId = toeId;
      if (contention !== undefined) payload.contention = contention;
      if (requestContentions !== undefined) payload.requestContentions = requestContentions;
      if (requestNotes !== undefined) payload.requestNotes = requestNotes;
      if (providerUserId !== undefined) payload.providerUserId = providerUserId;
      if (qaReviewerUserId !== undefined) payload.qaReviewerUserId = qaReviewerUserId;
      if (selectedEvidenceFileIds !== undefined) {
        payload.selectedEvidenceFileIds = selectedEvidenceFileIds;
      }
      if (aiFieldGenerationSettings !== undefined) {
        payload.aiFieldGenerationSettings = aiFieldGenerationSettings;
      }
      if (evidenceReviewPrompt !== undefined) payload.evidenceReviewPrompt = evidenceReviewPrompt;
      if (medicalOpinionParameters !== undefined) {
        payload.medicalOpinionParameters = medicalOpinionParameters;
      }
      if (autoGenerateMedicalOpinion !== undefined) {
        payload.autoGenerateMedicalOpinion = autoGenerateMedicalOpinion;
      }
      if (moPrompt !== undefined) payload.moPrompt = moPrompt;
      if (saveAsDraft !== undefined) payload.saveAsDraft = saveAsDraft;
      if (submitRequest !== undefined) payload.submitRequest = submitRequest;

      const response = await axios.put(`/dbq/user-dbq/${userDbqId}/request`, payload);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Update user DBQ form data
 */
export const useUpdateUserDbq = (onSuccess) => {
  return useMutation({
    mutationFn: async ({ userDbqId, formData, token = null }) => {
      const url = token
        ? `/dbq/public/user-dbq/${userDbqId}/${token}`
        : `/dbq/user-dbq/${userDbqId}`;
      const response = await axios.put(url, { formData });
      return response.data;
    },
    onSuccess,
  });
};

/**
 * Create a DBQ suggestion for a specific field
 */
export const useCreateDbqSuggestion = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      fieldId,
      suggestedValue,
      note = null,
      upsertPending = undefined,
    }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/suggestions`, {
        fieldId,
        suggestedValue,
        note,
        ...(typeof upsertPending === 'boolean' ? { upsertPending } : {}),
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const upsertDbqSuggestion = async ({
  userDbqId,
  fieldId,
  suggestedValue,
  note = null,
  sourceEvidenceRefs,
  transcriptRefs,
}) => {
  const response = await axios.post(`/dbq/user-dbq/${userDbqId}/suggestions`, {
    fieldId,
    suggestedValue,
    note,
    upsertPending: true,
    ...(Array.isArray(sourceEvidenceRefs) ? { sourceEvidenceRefs } : {}),
    ...(Array.isArray(transcriptRefs) ? { transcriptRefs } : {}),
  });
  return response.data;
};

/**
 * Save Case Manager inline suggestion drafts without mutating DBQ answer values.
 */
export const syncDbqInlineSuggestions = async (userDbqId, suggestions = []) => {
  const response = await axios.put(`/dbq/user-dbq/${userDbqId}/inline-suggestions`, {
    suggestions,
  });
  return response.data;
};

/**
 * Accept and apply a DBQ suggestion
 */
export const useAcceptDbqSuggestion = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      suggestionId,
      note = null,
      editedValue = undefined,
      sourceEvidenceRefs = undefined,
      transcriptRefs = undefined,
    }) => {
      const response = await axios.post(
        `/dbq/user-dbq/${userDbqId}/suggestions/${suggestionId}/accept`,
        {
          note,
          ...(typeof editedValue !== 'undefined' ? { editedValue } : {}),
          ...(Array.isArray(sourceEvidenceRefs) ? { sourceEvidenceRefs } : {}),
          ...(Array.isArray(transcriptRefs) ? { transcriptRefs } : {}),
        },
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Reject a DBQ suggestion
 */
export const useRejectDbqSuggestion = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ userDbqId, suggestionId, note = null }) => {
      const response = await axios.post(
        `/dbq/user-dbq/${userDbqId}/suggestions/${suggestionId}/reject`,
        { note },
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Dismiss a DBQ suggestion
 */
export const useDismissDbqSuggestion = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ userDbqId, suggestionId, note = null }) => {
      const response = await axios.post(
        `/dbq/user-dbq/${userDbqId}/suggestions/${suggestionId}/dismiss`,
        { note },
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Delete a user DBQ
 */
export const useDeleteUserDbq = (onSuccess) => {
  return useMutation({
    mutationFn: async (userDbqId) => {
      const response = await axios.delete(`/dbq/user-dbq/${userDbqId}`);
      return response.data;
    },
    onSuccess,
  });
};

/**
 * Generate PDF from user DBQ
 */
export const useGenerateDbqPdf = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async (userDbqId) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/generate-pdf`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Send email with link to edit user DBQ
 */
export const useSendDbqEmail = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      recipientEmail,
      includeComments = false,
      sendSms = false,
      recipientPhone = null,
      providerUserId = null,
    }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/send`, {
        recipientEmail,
        includeComments,
        sendSms,
        recipientPhone,
        providerUserId,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Send one provider email covering multiple DBQ requests on the same claim.
 */
export const useSendBulkDbqEmail = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      claimId,
      userDbqIds,
      providerUserId,
      includeComments = false,
      mode = 'send',
    }) => {
      const response = await axios.post(`/dbq/claim/${claimId}/send-provider-bulk`, {
        userDbqIds,
        providerUserId,
        includeComments,
        mode,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Request changes on a DBQ.
 */
export const useRequestDbqChanges = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      includeComments = false,
      sendSms = false,
      recipientPhone = null,
      note = null,
      target = null,
    }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/request-changes`, {
        includeComments,
        sendSms,
        recipientPhone,
        note,
        target,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Move a provider-completed DBQ into Claims Admin review.
 */
export const useMarkDbqReadyForQaReview = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async (userDbqId) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/mark-ready-for-review`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Mark selected Claims Admin-returned DBQs ready for review from the claim workspace.
 */
export const useBulkMarkDbqsReadyForQaReview = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ claimId, userDbqIds }) => {
      const response = await axios.post(`/dbq/claim/${claimId}/mark-ready-for-review`, {
        userDbqIds,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Notify selected Claims Admin users that a claim's DBQ conditions are ready for review.
 */
export const useSendClaimDbqsForQaReview = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ claimId, claimsAdminUserIds }) => {
      const response = await axios.post(`/dbq/claim/${claimId}/send-for-review`, {
        claimsAdminUserIds,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Mark a Claims Admin-reviewed DBQ finalized.
 */
export const useFinalizeDbqReview = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async (userDbqId) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/finalize`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useReopenFinalizedDbqReview = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      mode = 'return_to_review',
      target = null,
      note = '',
    }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/reopen`, {
        mode,
        target,
        note,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useRegenerateDbqSubmissionPacket = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async (userDbqId) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/submission-packet/regenerate`);
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const downloadDbqSubmissionPacket = async (userDbqId) => {
  try {
    const response = await axios.get(
      `/dbq/user-dbq/${userDbqId}/submission-packet/download`,
      { responseType: 'blob' },
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `dbq-submission-packet-${String(userDbqId || '').slice(0, 8)}.zip`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1];
      }
    }

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    toast.error(err?.response?.data?.msg || 'Failed to download submission packet');
    throw err;
  }
};

export const downloadAllDbqSubmissionPackets = async (claimId, userDbqIds = []) => {
  try {
    const response = await axios.post(
      `/dbq/claim/${claimId}/submission-packets/download`,
      { userDbqIds },
      { responseType: 'blob' },
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `claim-packets-${String(claimId || '').slice(0, 8)}.zip`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1];
      }
    }

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    toast.error(err?.response?.data?.msg || 'Failed to download all DBQ packets');
    throw err;
  }
};

/**
 * Trigger MO generation/regeneration for a user DBQ
 */
export const useRequestMoAutoGeneration = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({
      userDbqId,
      prompt = '',
    }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/mo/generate`, {
        prompt,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Fetch providers for a claim
 */
export const useFetchClaimProviders = (claimId) => {
  return useQuery({
    queryKey: ['claim-providers', claimId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/claim/${claimId}/providers`);
      return response.data;
    },
    enabled: !!claimId,
  });
};

/**
 * Fetch providers for a user DBQ's claim
 */
export const useFetchUserDbqProviders = (userDbqId) => {
  return useQuery({
    queryKey: ['user-dbq-providers', userDbqId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/${userDbqId}/providers`);
      return response.data;
    },
    enabled: !!userDbqId,
  });
};

/**
 * Update DBQ form template ID
 */
export const useUpdateDbqFormTemplate = (onSuccess) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, docusealTemplateId }) => {
      const response = await axios.put(`/dbq/forms/${dbqFormId}/template`, {
        docusealTemplateId,
      });
      return response.data;
    },
    onSuccess,
  });
};

/**
 * Update DBQ form template-level required fields (requires `admin:dbq_forms`)
 * Affects ALL user DBQs that use this form template
 */
export const useUpdateRequiredFields = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ dbqFormId, requiredFields }) => {
      const response = await axios.put(`/dbq/forms/${dbqFormId}/required-fields`, {
        requiredFields,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Update user DBQ's required fields (requires `admin:dbq_forms`)
 * Affects ONLY this specific user DBQ instance
 */
export const useUpdateUserDbqRequiredFields = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ userDbqId, requiredFields }) => {
      const response = await axios.put(`/dbq/user-dbq/${userDbqId}/required-fields`, {
        requiredFields,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Update user DBQ status (requires `admin:dbq_forms`)
 */
export const useUpdateUserDbqStatus = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ userDbqId, status }) => {
      const response = await axios.put(`/dbq/user-dbq/${userDbqId}/status`, {
        status,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Sign a DBQ form (provider action)
 */
export const useSignDbq = (onSuccess, onError) => {
  return useMutation({
    mutationFn: async ({ userDbqId, certificationData }) => {
      const response = await axios.post(`/dbq/user-dbq/${userDbqId}/sign`, {
        certificationData,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

/**
 * Fetch current provider's profile for signing
 * @param {boolean} enabled - Whether to enable the query (should only be true for providers)
 */
export const useFetchCurrentProviderProfile = (enabled = true) => {
  return useQuery({
    queryKey: ['current-provider-profile'],
    queryFn: async () => {
      const response = await axios.get('/dbq/provider/profile');
      return response.data;
    },
    enabled: enabled,
    refetchOnWindowFocus: false,
    staleTime: 60000, // Cache for 1 minute
  });
};

/**
 * Valid DBQ statuses
 */
export const DBQ_STATUSES = {
  draft: { label: 'Draft', variant: 'gray' },
  provider_ready: { label: 'Requested', variant: 'info' },
  corrections: { label: 'Corrections', variant: 'warning' },
  complete: { label: 'Complete', variant: 'success' },
};

export const DBQ_REQUEST_WORKFLOW_STATUSES = {
  draft_request: { label: 'Draft', variant: 'gray' },
  ready_to_send: { label: 'Ready to Send', variant: 'info' },
  sent_to_provider: { label: 'Sent to Provider', variant: 'primary' },
  provider_in_progress: { label: 'Provider In Progress', variant: 'primary' },
  completed: { label: 'Provider Completed', variant: 'success' },
  changes_requested: { label: 'Changes Requested', variant: 'warning' },
  in_qa_review: { label: 'Ready For Review', variant: 'dark' },
  finalized: { label: 'Finalized', variant: 'success' },
};
